import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity,
  ActivityIndicator, Image, Modal,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../src/api/client';
import { config } from '../../src/config';
import { useTheme } from '../../src/context/ThemeContext';
import { useNetwork } from '../../src/context/NetworkContext';
import { useToast } from '../../src/components/Toast';
import ContentContainer from '../../src/components/ContentContainer';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { rs } from '../../src/utils/responsive';
import PageFooter from '../../src/components/PageFooter';

interface ProductImage {
  id: string;
  imageUrl: string;
  sortOrder: number;
}

interface Product {
  id: string;
  name: string;
  description: string | null;
  priceInCents: number;
  imageUrl: string | null;
  images: ProductImage[];
}

// /uploads/ lives on the API host, not the frontend host. Mirrors admin/products.tsx.
const API_BASE = config.apiUrl;

export default function HardwareShop() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const { isOffline } = useNetwork();
  const router = useRouter();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState<Map<string, number>>(new Map());
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  useEffect(() => {
    api<Product[]>('/api/hardware/products')
      .then(setProducts)
      .catch((err: any) => {
        if (!isOffline) {
          toast.show({ type: 'error', title: 'Couldn’t load shop', message: err?.message ?? 'Try again in a moment.' });
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const updateQty = (id: string, delta: number) => {
    setCart(prev => {
      const next = new Map(prev);
      const current = next.get(id) || 0;
      const newQty = current + delta;
      if (newQty <= 0) next.delete(id);
      else next.set(id, newQty);
      return next;
    });
  };

  const totalItems = Array.from(cart.values()).reduce((s, q) => s + q, 0);
  const totalCents = Array.from(cart.entries()).reduce((s, [id, qty]) => {
    const p = products.find(p => p.id === id);
    return s + (p ? p.priceInCents * qty : 0);
  }, 0);

  const handleCheckout = () => {
    const cartParam = JSON.stringify(Array.from(cart.entries()).map(([id, qty]) => ({ id, qty })));
    router.push(`/owner/shop/checkout?cart=${encodeURIComponent(cartParam)}` as never);
  };

  const renderProduct = ({ item }: { item: Product }) => {
    const qty = cart.get(item.id) || 0;
    return (
      <View style={styles.card}>
        {item.images && item.images.length > 0 ? (
          <View style={styles.thumbRow}>
            {item.images.slice(0, 4).map(img => {
              const uri = `${API_BASE}${img.imageUrl}`;
              return (
                <TouchableOpacity key={img.id} onPress={() => setLightboxImage(uri)} style={styles.thumbWrap}>
                  <Image source={{ uri }} style={styles.thumb} resizeMode="cover" />
                </TouchableOpacity>
              );
            })}
          </View>
        ) : (
          <View style={styles.cardIcon}>
            <Ionicons name="hardware-chip" size={40} color={colors.primary} />
          </View>
        )}
        <View style={styles.cardContent}>
          <Text style={styles.productName}>{item.name}</Text>
          {item.description && <Text style={styles.productDesc}>{item.description}</Text>}
          <Text style={styles.productPrice}>${(item.priceInCents / 100).toFixed(2)}</Text>
        </View>
        <View style={styles.qtyRow}>
          {qty > 0 ? (
            <>
              <TouchableOpacity style={styles.qtyBtn} onPress={() => updateQty(item.id, -1)}>
                <Ionicons name="remove" size={18} color={colors.primary} />
              </TouchableOpacity>
              <Text style={styles.qtyText}>{qty}</Text>
              <TouchableOpacity style={styles.qtyBtn} onPress={() => updateQty(item.id, 1)}>
                <Ionicons name="add" size={18} color={colors.primary} />
              </TouchableOpacity>
            </>
          ) : (
            <TouchableOpacity style={styles.addBtn} onPress={() => updateQty(item.id, 1)}>
              <Ionicons name="cart" size={16} color="#fff" />
              <Text style={styles.addBtnText}>Add to Cart</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  return (
    <ContentContainer role="owner" footer={false}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Hardware Shop</Text>
          <View style={styles.headerLinks}>
            <TouchableOpacity onPress={() => router.push('/owner/guides/installation' as never)}>
              <Text style={styles.ordersLink}>Installation Guide</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => router.push('/owner/shop/orders' as never)}>
              <Text style={styles.ordersLink}>Order History</Text>
            </TouchableOpacity>
          </View>
        </View>

        {loading ? (
          <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
        ) : products.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="cube-outline" size={64} color={colors.textLight} />
            <Text style={styles.emptyTitle}>No Products Available</Text>
            <Text style={styles.emptyText}>Check back later for available hardware.</Text>
          </View>
        ) : (
          <FlatList
            ListFooterComponent={<PageFooter />}
            data={products}
            keyExtractor={item => item.id}
            renderItem={renderProduct}
            contentContainerStyle={{ paddingBottom: totalItems > 0 ? 100 : spacing.xl }}
          />
        )}

        <Modal
          visible={lightboxImage !== null}
          transparent
          animationType="fade"
          onRequestClose={() => setLightboxImage(null)}
        >
          <TouchableOpacity
            style={styles.lightboxBackdrop}
            activeOpacity={1}
            onPress={() => setLightboxImage(null)}
          >
            {lightboxImage && (
              <Image source={{ uri: lightboxImage }} style={styles.lightboxImage} resizeMode="contain" />
            )}
            <TouchableOpacity style={styles.lightboxClose} onPress={() => setLightboxImage(null)}>
              <Ionicons name="close" size={32} color="#fff" />
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>

        {totalItems > 0 && (
          <View style={styles.cartBar}>
            <View>
              <Text style={styles.cartCount}>{totalItems} item{totalItems !== 1 ? 's' : ''}</Text>
              <Text style={styles.cartTotal}>${(totalCents / 100).toFixed(2)}</Text>
            </View>
            <TouchableOpacity style={styles.checkoutBtn} onPress={handleCheckout}>
              <Text style={styles.checkoutText}>Proceed to Checkout</Text>
              <Ionicons name="arrow-forward" size={18} color="#fff" />
            </TouchableOpacity>
          </View>
        )}
      </View>
    </ContentContainer>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, padding: spacing.md },
  header: { marginBottom: spacing.md },
  headerLinks: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.xs },
  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  ordersLink: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.semibold },
  card: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    marginBottom: spacing.sm, borderWidth: 1, borderColor: c.borderLight,
  },
  cardIcon: { alignItems: 'center', marginBottom: spacing.sm },
  thumbRow: { flexDirection: 'row', gap: spacing.xs, marginBottom: spacing.sm },
  thumbWrap: { width: 64, height: 64, borderRadius: borderRadius.sm, overflow: 'hidden', backgroundColor: c.surfaceSecondary },
  thumb: { width: '100%', height: '100%' },
  lightboxBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.92)', justifyContent: 'center', alignItems: 'center' },
  lightboxImage: { width: '100%', height: '100%' },
  lightboxClose: { position: 'absolute', top: 40, right: 20, padding: spacing.sm },
  cardContent: { marginBottom: spacing.sm },
  productName: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  productDesc: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 4 },
  productPrice: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.primary, marginTop: spacing.xs },
  qtyRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.md },
  qtyBtn: {
    width: rs(36), height: rs(36), borderRadius: rs(18), borderWidth: 1, borderColor: c.primary,
    justifyContent: 'center', alignItems: 'center',
  },
  qtyText: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, minWidth: 30, textAlign: 'center' },
  addBtn: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    backgroundColor: c.primary, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm,
    borderRadius: borderRadius.md,
  },
  addBtnText: { color: '#fff', fontWeight: fontWeight.semibold, fontSize: fontSize.sm },
  emptyState: { alignItems: 'center', paddingTop: rs(80) },
  emptyTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.md },
  emptyText: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', maxWidth: 300, marginTop: spacing.xs },
  cartBar: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    backgroundColor: c.surface, borderTopWidth: 1, borderTopColor: c.borderLight,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
  },
  cartCount: { fontSize: fontSize.sm, color: c.textSecondary },
  cartTotal: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  checkoutBtn: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    backgroundColor: c.primary, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm,
    borderRadius: borderRadius.md,
  },
  checkoutText: { color: '#fff', fontWeight: fontWeight.semibold, fontSize: fontSize.sm },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
